/**
 * Shared shader code for globe morphing effects.
 *
 * The globe uses a morphing effect that interpolates between two geometries:
 * - Sphere (curved, hemisphere view)
 * - Flat plane (map view)
 *
 * The `curvature` uniform controls this interpolation (0 = flat, 1 = sphere).
 * Both positions are stored as vertex attributes and interpolated on the GPU.
 */

// =============================================================================
// Vertex Shader Components
// =============================================================================

/**
 * Attribute declaration for the flat position.
 * The sphere position is the default `position` attribute.
 */
export const FLAT_POSITION_ATTRIBUTE = `attribute vec3 flatPosition;`

/**
 * Curvature uniform declaration.
 * 0 = completely flat, 1 = full sphere curvature.
 */
export const CURVATURE_UNIFORM = `uniform float curvature;`

/**
 * Core morphing calculation - interpolates between flat and sphere positions.
 * Requires: flatPosition attribute, curvature uniform, position attribute (built-in)
 */
export const MORPH_CALCULATION = `vec3 morphed = mix(flatPosition, position, curvature);`

/**
 * Standard gl_Position calculation for morphed geometry.
 */
export const MORPHED_POSITION = `gl_Position = projectionMatrix * modelViewMatrix * vec4(morphed, 1.0);`

// =============================================================================
// Complete Vertex Shaders
// =============================================================================

/**
 * Basic vertex shader with morphing - no textures, no varyings.
 * Suitable for solid color rendering.
 */
export const basicMorphVertexShader = `
  ${FLAT_POSITION_ATTRIBUTE}
  ${CURVATURE_UNIFORM}

  void main() {
    ${MORPH_CALCULATION}
    ${MORPHED_POSITION}
  }
`

/**
 * Vertex shader with morphing and UV passthrough.
 * Suitable for single-texture rendering.
 */
export const texturedMorphVertexShader = `
  ${FLAT_POSITION_ATTRIBUTE}
  ${CURVATURE_UNIFORM}
  varying vec2 vUv;

  void main() {
    vUv = uv;
    ${MORPH_CALCULATION}
    ${MORPHED_POSITION}
  }
`

/**
 * Vertex shader with morphing, UV transform, and screen position.
 * Suitable for tile rendering with UV offset/scale.
 */
export const tiledMorphVertexShader = `
  ${FLAT_POSITION_ATTRIBUTE}
  ${CURVATURE_UNIFORM}
  uniform vec2 uvOffset;
  uniform float uvScale;
  varying vec2 vUv;

  void main() {
    vUv = uvOffset + uv * uvScale;
    ${MORPH_CALCULATION}
    ${MORPHED_POSITION}
  }
`

/**
 * Vertex shader with morphing, UV, and screen position for specular effects.
 * Suitable for the main country globe with visual effects.
 */
export const countryGlobeMorphVertexShader = `
  ${FLAT_POSITION_ATTRIBUTE}
  ${CURVATURE_UNIFORM}
  varying vec2 vUv;
  varying vec2 vScreenPos;

  void main() {
    vUv = uv;
    ${MORPH_CALCULATION}
    vec4 clipPos = projectionMatrix * modelViewMatrix * vec4(morphed, 1.0);
    gl_Position = clipPos;
    vScreenPos = clipPos.xy / clipPos.w;
  }
`

// =============================================================================
// Fragment Shaders
// =============================================================================

/**
 * Basic solid color fragment shader.
 */
export const solidColorFragmentShader = `
  uniform vec3 color;

  void main() {
    gl_FragColor = vec4(color, 1.0);
  }
`

/**
 * Solid color fragment shader with opacity.
 */
export const solidColorWithOpacityFragmentShader = `
  uniform vec3 color;
  uniform float opacity;

  void main() {
    gl_FragColor = vec4(color, opacity);
  }
`

/**
 * Basic texture sampling fragment shader.
 */
export const texturedFragmentShader = `
  uniform sampler2D map;
  varying vec2 vUv;

  void main() {
    gl_FragColor = texture2D(map, vUv);
  }
`
